import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, Package, Clock, CheckCircle, XCircle, Truck, Ban, ExternalLink } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DataPagination } from '@/components/DataPagination';
import { format } from 'date-fns';
import { ViralRemakeBadge, isViralRemake, MultiProductBadge, isMultiProduct, TiktokCollabTypeBadge } from '@/components/ViralRemakeBadge';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

type TabFilter = 'all' | 'pending' | 'approved' | 'rejected' | 'expired' | 'shipped' | 'cancelled';

type SampleApplication = {
  id: string;
  tiktok_application_id: string;
  creator_open_id: string | null;
  creator_username: string | null;
  creator_nickname: string | null;
  product_tiktok_id: string | null;
  product_title: string | null;
  order_id: string | null;
  matched_hire_id: string;
  matched_campaign_id: string | null;
  status: string | null;
  fulfillment_status: string | null;
  tracking_number: string | null;
  is_approvable: boolean | null;
  disapprovable_reasons: any;
  commission_rate: string | null;
  available_quantity: number | null;
  partner_name: string | null;
  approve_expiration_time: string | null;
  shipment_expiration_time: string | null;
  first_seen_at: string;
  status_changed_at: string;
  last_synced_at: string;
  brand_configs?: { id: string; brand_name: string } | null;
  // is_viral_remake / is_multi_product / tiktok_collab_type / embedded
  // cr_campaign_products added 2026-09-25 — see the "Job" column, which shows
  // what type of job this sample was requested against.
  cr_campaigns?: {
    id: string;
    name: string | null;
    product_name: string | null;
    is_viral_remake?: boolean | null;
    is_multi_product?: boolean | null;
    tiktok_collab_type?: string | null;
    cr_campaign_products?: { id: string }[];
  } | null;
};

function StatusBadge({ status }: { status?: string | null }) {
  if (!status) return <Badge variant="outline">Unknown</Badge>;
  const s = status.toUpperCase();
  if (s === 'APPROVED')                          return <Badge className="bg-green-100 text-green-800 border border-green-200">Approved</Badge>;
  if (s === 'PENDING')                           return <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-200">Pending</Badge>;
  if (s === 'REJECTED' || s === 'DISAPPROVED')   return <Badge className="bg-red-100 text-red-700 border border-red-200">Rejected</Badge>;
  if (s === 'EXPIRED')                           return <Badge className="bg-gray-100 text-gray-600 border border-gray-200">Expired</Badge>;
  if (s === 'SHIPPED')                           return <Badge className="bg-blue-100 text-blue-800 border border-blue-200">Shipped</Badge>;
  if (s.includes('CANCEL'))                      return <Badge className="bg-slate-100 text-slate-600 border border-slate-200">Cancelled</Badge>;
  return <Badge variant="outline" className="capitalize">{status.toLowerCase()}</Badge>;
}

function FulfillmentBadge({ status }: { status?: string | null }) {
  if (!status) return <span className="text-muted-foreground text-xs italic">–</span>;
  const s = status.toUpperCase();
  if (s === 'ONGOING')   return <Badge className="bg-blue-100 text-blue-800 border border-blue-200">Ongoing</Badge>;
  if (s === 'COMPLETED' || s === 'DELIVERED') return <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">Delivered</Badge>;
  return <Badge variant="outline" className="capitalize">{status.toLowerCase()}</Badge>;
}

export default function SampleRequests() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<TabFilter>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selected, setSelected] = useState<SampleApplication | null>(null);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  function handleTabChange(tab: TabFilter) {
    setActiveTab(tab);
    setPage(1);
  }

  const { data, isLoading } = useQuery({
    queryKey: ['sample-applications', activeTab, search, page, pageSize],
    queryFn: async () => {
      let query = supabase
        .from('tiktok_sample_applications')
        .select(
          '*, brand_configs(id, brand_name), ' +
          'cr_campaigns(id, name, product_name, is_viral_remake, is_multi_product, tiktok_collab_type, cr_campaign_products(id))',
          { count: 'exact' }
        )
        .order('first_seen_at', { ascending: false });

      if (activeTab === 'rejected') {
        query = query.or('status.ilike.%rejected%,status.ilike.%disapprov%');
      } else if (activeTab === 'cancelled') {
        query = query.ilike('status', '%cancel%');
      } else if (activeTab !== 'all') {
        // Statuses aren't consistently upper-cased in the source data
        // (e.g. "Shipped", "Withdraw_cancelled"), so match case-insensitively.
        query = query.ilike('status', `%${activeTab}%`);
      }

      if (search.trim()) {
        const q = search.trim();
        query = query.or(
          `creator_username.ilike.%${q}%,creator_nickname.ilike.%${q}%,product_title.ilike.%${q}%,order_id.ilike.%${q}%`
        );
      }

      const { data: rows, count, error } = await query
        .range((page - 1) * pageSize, page * pageSize - 1);
      if (error) throw error;
      return { rows: (rows ?? []) as SampleApplication[], total: count ?? 0 };
    },
  });

  const { data: counts } = useQuery({
    queryKey: ['sample-applications-counts'],
    queryFn: async () => {
      const { data, error } = await supabase.from('tiktok_sample_applications').select('status');
      if (error) throw error;
      const all = data ?? [];
      const includesStatus = (needle: string) => all.filter((r: any) => (r.status ?? '').toLowerCase().includes(needle)).length;
      return {
        total: all.length,
        pending: includesStatus('pending'),
        approved: includesStatus('approved'),
        rejected: includesStatus('rejected') + includesStatus('disapprov'),
        expired: includesStatus('expired'),
        shipped: includesStatus('shipped'),
        cancelled: includesStatus('cancel'),
      };
    },
    staleTime: 30_000,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="h-full flex flex-col gap-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Sample Requests</h1>
        <p className="text-muted-foreground text-sm">TikTok sample requests across all hired creators</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4 shrink-0">
        {([
          { label: 'Total',     value: counts?.total,     color: '',                       icon: <Package className="h-4 w-4" /> },
          { label: 'Pending',   value: counts?.pending,   color: 'text-yellow-600',        icon: <Clock className="h-4 w-4" /> },
          { label: 'Approved',  value: counts?.approved,  color: 'text-green-600',         icon: <CheckCircle className="h-4 w-4" /> },
          { label: 'Shipped',   value: counts?.shipped,   color: 'text-blue-600',          icon: <Truck className="h-4 w-4" /> },
          { label: 'Rejected',  value: counts?.rejected,  color: 'text-red-600',           icon: <XCircle className="h-4 w-4" /> },
          { label: 'Cancelled', value: counts?.cancelled, color: 'text-slate-500',         icon: <Ban className="h-4 w-4" /> },
          { label: 'Expired',   value: counts?.expired,   color: 'text-muted-foreground',  icon: <Clock className="h-4 w-4" /> },
        ] as const).map(({ label, value, color, icon }) => (
          <Card key={label}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                {icon} {label}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={`text-2xl font-bold ${color}`}>{value ?? '–'}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="flex-1 flex flex-col min-h-0">
        <CardContent className="pt-4 flex-1 flex flex-col min-h-0">
          <div className="flex flex-col gap-3 mb-4 shrink-0 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-4">
            <Tabs value={activeTab} onValueChange={v => handleTabChange(v as TabFilter)}>
              <TabsList>
                <TabsTrigger value="all">All ({counts?.total ?? 0})</TabsTrigger>
                <TabsTrigger value="pending">Pending ({counts?.pending ?? 0})</TabsTrigger>
                <TabsTrigger value="approved">Approved ({counts?.approved ?? 0})</TabsTrigger>
                <TabsTrigger value="shipped">Shipped ({counts?.shipped ?? 0})</TabsTrigger>
                <TabsTrigger value="rejected">Rejected ({counts?.rejected ?? 0})</TabsTrigger>
                <TabsTrigger value="cancelled">Cancelled ({counts?.cancelled ?? 0})</TabsTrigger>
                <TabsTrigger value="expired">Expired ({counts?.expired ?? 0})</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative w-72">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by creator, product, or order..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>

          <div className="flex-1 overflow-auto min-h-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Creator</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>First Seen</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">Loading...</TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                      {search ? 'No sample requests match your search' : activeTab === 'all' ? 'No sample requests found' : `No ${activeTab} sample requests`}
                    </TableCell>
                  </TableRow>
                ) : rows.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelected(r)}>
                    <TableCell>
                      <div className="font-medium">{r.creator_nickname || r.creator_username || '–'}</div>
                      {r.creator_username && r.creator_nickname && (
                        <div className="text-xs text-muted-foreground">@{r.creator_username}</div>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate text-sm">{r.product_title || '–'}</TableCell>
                    <TableCell className="text-sm">{r.brand_configs?.brand_name ?? <span className="text-muted-foreground italic">–</span>}</TableCell>
                    <TableCell className="max-w-[220px]">
                      {/* Badges stack UNDER the name, never beside it — see
                          RosterCampaigns.tsx (2026-09-25): shrink-0 badges on
                          one unwrapped flex line overran the cell's advisory
                          max-width and bled into the next column instead of
                          clipping. */}
                      <div className="truncate text-sm">
                        {r.cr_campaigns?.name || r.cr_campaigns?.product_name || <span className="text-muted-foreground italic">–</span>}
                      </div>
                      {r.cr_campaigns && (isViralRemake(r.cr_campaigns) || isMultiProduct(r.cr_campaigns) || r.cr_campaigns.tiktok_collab_type) && (
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          {isViralRemake(r.cr_campaigns) && <ViralRemakeBadge className="shrink-0" />}
                          {isMultiProduct(r.cr_campaigns) && <MultiProductBadge className="shrink-0" count={r.cr_campaigns.cr_campaign_products!.length} />}
                          <TiktokCollabTypeBadge value={r.cr_campaigns.tiktok_collab_type} className="shrink-0" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell><StatusBadge status={r.status} /></TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {r.first_seen_at ? format(new Date(r.first_seen_at), 'MMM d, yyyy') : '–'}
                    </TableCell>
                    <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                      {r.matched_campaign_id && (
                        <Link
                          to={`/roster-campaigns/${r.matched_campaign_id}`}
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                        >
                          View Job <ExternalLink className="h-3 w-3" />
                        </Link>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <DataPagination
            currentPage={page}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={total}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            className="mt-auto shrink-0"
          />
        </CardContent>
      </Card>

      {/* Detail Sheet */}
      <Sheet open={!!selected} onOpenChange={(open) => { if (!open) setSelected(null); }}>
        <SheetContent className="overflow-y-auto w-full sm:max-w-lg">
          {selected && (
            <>
              <SheetHeader className="pb-4">
                <SheetTitle className="text-lg">{selected.creator_nickname || selected.creator_username || 'Sample Request'}</SheetTitle>
                <div className="pt-1 flex items-center gap-2">
                  <StatusBadge status={selected.status} />
                  <FulfillmentBadge status={selected.fulfillment_status} />
                </div>
              </SheetHeader>

              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-0.5">
                    <p className="text-xs text-muted-foreground">Creator Handle</p>
                    <p className="font-medium">{selected.creator_username ? `@${selected.creator_username}` : '–'}</p>
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-xs text-muted-foreground">Brand</p>
                    <p className="font-medium">{selected.brand_configs?.brand_name ?? '–'}</p>
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-xs text-muted-foreground">Product</p>
                    <p className="font-medium">{selected.product_title ?? '–'}</p>
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-xs text-muted-foreground">Order ID</p>
                    <p className="font-medium font-mono">{selected.order_id ?? '–'}</p>
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-xs text-muted-foreground">Tracking Number</p>
                    <p className="font-medium font-mono">{selected.tracking_number ?? '–'}</p>
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-xs text-muted-foreground">Commission Rate</p>
                    <p className="font-medium">{selected.commission_rate ?? '–'}</p>
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-xs text-muted-foreground">Partner</p>
                    <p className="font-medium">{selected.partner_name ?? '–'}</p>
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-xs text-muted-foreground">Available Qty</p>
                    <p className="font-medium">{selected.available_quantity ?? '–'}</p>
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-xs text-muted-foreground">First Seen</p>
                    <p className="font-medium">{selected.first_seen_at ? format(new Date(selected.first_seen_at), 'MMM d, yyyy h:mm a') : '–'}</p>
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-xs text-muted-foreground">Status Changed</p>
                    <p className="font-medium">{selected.status_changed_at ? format(new Date(selected.status_changed_at), 'MMM d, yyyy h:mm a') : '–'}</p>
                  </div>
                  {selected.approve_expiration_time && (
                    <div className="space-y-0.5">
                      <p className="text-xs text-muted-foreground">Approve Expires</p>
                      <p className="font-medium">{format(new Date(selected.approve_expiration_time), 'MMM d, yyyy h:mm a')}</p>
                    </div>
                  )}
                  {selected.shipment_expiration_time && (
                    <div className="space-y-0.5">
                      <p className="text-xs text-muted-foreground">Shipment Expires</p>
                      <p className="font-medium">{format(new Date(selected.shipment_expiration_time), 'MMM d, yyyy h:mm a')}</p>
                    </div>
                  )}
                </div>

                {selected.disapprovable_reasons && (
                  <>
                    <Separator />
                    <div className="space-y-1.5">
                      <p className="text-sm font-semibold">Disapprovable Reasons</p>
                      <pre className="text-xs bg-muted rounded-md p-2 overflow-x-auto whitespace-pre-wrap break-words">
                        {JSON.stringify(selected.disapprovable_reasons, null, 2)}
                      </pre>
                    </div>
                  </>
                )}

                <Separator />
                <div className="space-y-2">
                  <p className="text-sm font-semibold">Actions</p>
                  {selected.matched_campaign_id ? (
                    <Link
                      to={`/roster-campaigns/${selected.matched_campaign_id}`}
                      className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
                    >
                      View Job <ExternalLink className="h-3.5 w-3.5" />
                    </Link>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No matching job found</p>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
